import React, { Suspense } from 'react'
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom'

import PageViewedAnalytics from 'utils/analytics'
import FlowProvider from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { FunnelProvider } from 'utils/FunnelContext'
import { maintenanceScreen } from 'constants/feature-keys'
import { PrivateRoute } from 'utils/PrivateRoute'
import ScrollToTop from 'utils/scrollToTop'
import { FunnelContentProvider } from 'components/templates/FunnelContent/FunnelContentProvider'
import { AddressProvider } from 'pages/ShippingAddress/AddressContext'
import { useFeature } from '@acorns/web-toggles-react'
import CenteredSpinner from 'components/CenteredSpinner/CenteredSpinner'
import { Welcome } from 'pages/Welcome'
import { Signup } from 'pages/Signup'
import { Maintenance } from 'pages/Maintenance'

import {
  BankLinkSuccess,
  ChooseFundingSource,
  ContactInfo,
  DateOfBirth,
  Funding,
  HDYHAU,
  DirectBankIntegration,
  DirectBankIntegrationRedirect,
  InvestInterstitial,
  InvestorQuestions,
  LaterInterstitial,
  LaterIraRecommendation,
  LaterSuitabilityQuestions,
  ManualFundingSource,
  MfaSetup,
  PersonalInfo,
  PhoneNumber,
  PlaidLinkRedirect,
  AboutPortfolio,
  PreviewPortfolio,
  PricingInterstitial,
  ProductOnboarding,
  RoundUpsFunding,
  RoundUpsInterstitial,
  RoundUpsPast,
  RoundUpsSetup,
  SSN,
  OneTimeInvestment,
  RecurringInvestment,
  SubscriptionActive,
  Success,
  SuitabilityQuestion,
  SuitabilityQuestions,
  AddressPreview,
  AddressSuggestion,
  AddressEdit,
  AddressInvalid,
  Institutions,
} from './lazy/pages'
import { usePreloadLazyComponents } from './lazy/index'
import { TrackAppLoadFinish } from 'utils/analytics/TrackAppLoadFinish'

function MainRouter() {
  const [maintenanceScreenEnabled] = useFeature(maintenanceScreen)

  // Preloads all code split routes
  usePreloadLazyComponents()

  return (
    <BrowserRouter>
      <FunnelProvider>
        <FlowProvider>
          <AddressProvider>
            <FunnelContentProvider>
              <PageViewedAnalytics />
              <ScrollToTop>
                <Suspense fallback={<CenteredSpinner />}>
                  <TrackAppLoadFinish />
                  <Switch>
                    <Route
                      path={FlowRoute.Welcome}
                      exact={true}
                      component={maintenanceScreenEnabled ? Maintenance : Welcome}
                    />

                    <Route
                      path={FlowRoute.Signup}
                      component={maintenanceScreenEnabled ? Maintenance : Signup}
                    />

                    <Route
                      path={FlowRoute.DirectBankIntegrationRedirect}
                      component={DirectBankIntegrationRedirect}
                    />

                    <Route path={FlowRoute.PlaidLinkRedirect} component={PlaidLinkRedirect} />

                    <PrivateRoute path={FlowRoute.Funding} component={Funding} />
                    <PrivateRoute path={FlowRoute.ContactInfo} component={ContactInfo} />
                    <PrivateRoute path={FlowRoute.Institutions} component={Institutions} />
                    <PrivateRoute
                      path={FlowRoute.ManualFundingSource}
                      component={ManualFundingSource}
                    />

                    <PrivateRoute path={FlowRoute.DateOfBirth} component={DateOfBirth} />

                    {/* Direct Bank Integration */}
                    <PrivateRoute
                      path={FlowRoute.DirectBankIntegration}
                      component={DirectBankIntegration}
                    />

                    {/* Plaid Flow */}
                    <PrivateRoute path={FlowRoute.BankLinkSuccess} component={BankLinkSuccess} />
                    <PrivateRoute
                      path={FlowRoute.ChooseFundingSource}
                      component={ChooseFundingSource}
                    />
                    <PrivateRoute
                      path={FlowRoute.InvestInterstitial}
                      component={InvestInterstitial}
                    />
                    <PrivateRoute
                      path={FlowRoute.InvestorQuestions}
                      component={InvestorQuestions}
                    />
                    <PrivateRoute path={FlowRoute.MFASetup} component={MfaSetup} />
                    <PrivateRoute path={FlowRoute.PersonalInfo} component={PersonalInfo} />

                    <PrivateRoute path={FlowRoute.PreviewPortfolio} component={PreviewPortfolio} />
                    <PrivateRoute path={FlowRoute.AboutPortfolio} component={AboutPortfolio} />
                    <PrivateRoute
                      path={FlowRoute.PricingInterstitial}
                      component={PricingInterstitial}
                    />
                    <PrivateRoute
                      path={FlowRoute.RecurringInvestment}
                      component={RecurringInvestment}
                    />
                    <PrivateRoute
                      path={FlowRoute.OneTimeInvestment}
                      component={OneTimeInvestment}
                    />

                    <PrivateRoute
                      path={FlowRoute.RoundUpsInterstitial}
                      component={RoundUpsInterstitial}
                    />
                    <PrivateRoute path={FlowRoute.RoundUpsSetup} component={RoundUpsSetup} />
                    <PrivateRoute path={FlowRoute.RoundUpsFunding} component={RoundUpsFunding} />
                    <PrivateRoute path={FlowRoute.RoundUpsPast} component={RoundUpsPast} />
                    <PrivateRoute path={FlowRoute.SSN} component={SSN} />
                    <PrivateRoute path={FlowRoute.Success} component={Success} />

                    <PrivateRoute
                      path={FlowRoute.SuitabilityQuestions}
                      component={SuitabilityQuestions}
                    />
                    <PrivateRoute
                      path={FlowRoute.EmploymentStatus}
                      component={SuitabilityQuestion}
                    />
                    <PrivateRoute path={FlowRoute.MaritalStatus} component={SuitabilityQuestion} />
                    <PrivateRoute path={FlowRoute.TaxFilingType} component={SuitabilityQuestion} />
                    <PrivateRoute path={FlowRoute.InvestmentGoal} component={SuitabilityQuestion} />
                    <PrivateRoute
                      path={FlowRoute.FinancialEducation}
                      component={SuitabilityQuestion}
                    />
                    <PrivateRoute path={FlowRoute.NetWorth} component={SuitabilityQuestion} />
                    <PrivateRoute path={FlowRoute.AnnualIncome} component={SuitabilityQuestion} />
                    <PrivateRoute
                      path={FlowRoute.CombinedAnnualIncome}
                      component={SuitabilityQuestion}
                    />
                    <PrivateRoute path={FlowRoute.Employees} component={SuitabilityQuestion} />
                    <PrivateRoute
                      path={FlowRoute.SelfEmployedIncome}
                      component={SuitabilityQuestion}
                    />
                    <PrivateRoute path={FlowRoute.KidsInvesting} component={SuitabilityQuestion} />
                    <Redirect
                      to={`${FlowRoute.SuitabilityQuestionsRoot}/0`}
                      from={FlowRoute.SuitabilityQuestionsRoot}
                    />

                    <PrivateRoute
                      path={FlowRoute.LaterSuitabilityQuestions}
                      component={LaterSuitabilityQuestions}
                    />
                    <PrivateRoute
                      path={FlowRoute.LaterIraRecommendation}
                      component={LaterIraRecommendation}
                    />

                    <PrivateRoute path={FlowRoute.HDYHAU} component={HDYHAU} />

                    <PrivateRoute
                      path={FlowRoute.ProductOnboarding}
                      component={ProductOnboarding}
                    />

                    <PrivateRoute path={FlowRoute.AddressEdit} component={AddressEdit} />
                    <PrivateRoute path={FlowRoute.AddressPreview} component={AddressPreview} />
                    <PrivateRoute path={FlowRoute.AddressInvalid} component={AddressInvalid} />
                    <PrivateRoute
                      path={FlowRoute.AddressSuggestion}
                      component={AddressSuggestion}
                    />

                    <PrivateRoute
                      path={FlowRoute.SubscriptionActive}
                      component={SubscriptionActive}
                    />

                    <PrivateRoute path={FlowRoute.PhoneNumber} component={PhoneNumber} />

                    <PrivateRoute
                      path={FlowRoute.LaterInterstitial}
                      component={LaterInterstitial}
                    />

                    <Redirect to={{ pathname: FlowRoute.Signup }} />
                  </Switch>
                </Suspense>
              </ScrollToTop>
            </FunnelContentProvider>
          </AddressProvider>
        </FlowProvider>
      </FunnelProvider>
    </BrowserRouter>
  )
}

export default MainRouter
